import { useState, useEffect } from 'react';
import { Loader2, Building2, Save } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  useLegalEntities,
  useCreateLegalEntity,
  useUpdateLegalEntity,
} from '@/hooks/use-comply';
import { handleApiError } from '@/lib/api-client';
import type { CreateLegalEntityPayload, LegalStructure } from '@/lib/types';

export function LegalEntityPage() {
  const { data: entities, isLoading } = useLegalEntities();
  const createMutation = useCreateLegalEntity();
  const updateMutation = useUpdateLegalEntity();

  const existing = entities?.[0] ?? null;

  const [form, setForm] = useState<CreateLegalEntityPayload>({
    registered_name: '',
    trading_name: '',
    registration_number: '',
    registration_country: '',
    registered_address: '',
    vat_number: '',
    website: '',
    date_incorporated: '',
    legal_structure: 'limited_company',
  });

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (existing) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setForm({
        registered_name: existing.registered_name,
        trading_name: existing.trading_name,
        registration_number: existing.registration_number,
        registration_country: existing.registration_country,
        registered_address: existing.registered_address,
        vat_number: existing.vat_number,
        website: existing.website,
        date_incorporated: existing.date_incorporated ?? '',
        legal_structure: existing.legal_structure,
      });
    }
  }, [existing]);

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSuccess(false);
  };

  const requiredFields: (keyof CreateLegalEntityPayload)[] = [
    'registered_name',
    'registration_number',
    'registration_country',
    'registered_address',
  ];
  const missingFields = requiredFields.filter((f) => !form[f]);
  const completeness = Math.round(
    ((requiredFields.length - missingFields.length) / requiredFields.length) * 100
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (missingFields.length > 0) {
      setError(
        `Required fields missing: ${missingFields.map((f) => f.replace(/_/g, ' ')).join(', ')}`
      );
      return;
    }

    try {
      const payload = {
        ...form,
        date_incorporated: form.date_incorporated || null,
      };
      if (existing) {
        await updateMutation.mutateAsync({ id: existing.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      setSuccess(true);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Legal Entity Details
          </h1>
          <p className="text-gray-600 mt-1">
            Organizational identity and registration information
          </p>
        </div>
        <div className="flex items-center gap-2">
          {completeness === 100 ? (
            <Badge className="bg-green-100 text-green-800">Complete</Badge>
          ) : (
            <Badge className="bg-yellow-100 text-yellow-800">
              {completeness}% Complete
            </Badge>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 p-3 rounded-lg text-sm">
            Legal entity details saved successfully.
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Company Registration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="registered_name">Registered Name *</Label>
                <Input
                  id="registered_name"
                  value={form.registered_name}
                  onChange={(e) => updateField('registered_name', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="trading_name">Trading Name</Label>
                <Input
                  id="trading_name"
                  value={form.trading_name}
                  onChange={(e) => updateField('trading_name', e.target.value)}
                  placeholder="If different from registered name"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="registration_number">Registration Number *</Label>
                <Input
                  id="registration_number"
                  value={form.registration_number}
                  onChange={(e) => updateField('registration_number', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="registration_country">Registration Country *</Label>
                <Input
                  id="registration_country"
                  value={form.registration_country}
                  onChange={(e) => updateField('registration_country', e.target.value)}
                  required
                  placeholder="e.g. United Kingdom"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="registered_address">Registered Address *</Label>
              <Textarea
                id="registered_address"
                value={form.registered_address}
                onChange={(e) => updateField('registered_address', e.target.value)}
                rows={3}
                required
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Additional Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="vat_number">VAT Number</Label>
                <Input
                  id="vat_number"
                  value={form.vat_number}
                  onChange={(e) => updateField('vat_number', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="url"
                  value={form.website}
                  onChange={(e) => updateField('website', e.target.value)}
                  placeholder="https://"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date_incorporated">Date Incorporated</Label>
                <Input
                  id="date_incorporated"
                  type="date"
                  value={form.date_incorporated ?? ''}
                  onChange={(e) => updateField('date_incorporated', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="legal_structure">Legal Structure</Label>
                <Select
                  value={form.legal_structure}
                  onValueChange={(v) =>
                    updateField('legal_structure', v as LegalStructure)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="limited_company">Limited Company</SelectItem>
                    <SelectItem value="llc">LLC</SelectItem>
                    <SelectItem value="sole_trader">Sole Trader</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                    <SelectItem value="corporation">Corporation</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button type="submit" disabled={isPending}>
            {isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {existing ? 'Update' : 'Save'} Legal Entity
          </Button>
        </div>
      </form>
    </div>
  );
}
